/**
 * Bind stored-board query input and DnD to one monotonically increasing render generation.
 *
 * The shape mirrors Resourcerer's table guard: the generation is bumped in capture
 * phase, transported by a deferred Livewire set in the same commit and echoed on
 * the content wrapper. Older concurrent responses never morph over a newer view.
 */
export default function storedTaskBoardWorkspace() {
  let wireId = null
  let stopMorphGuard = null

  return {
    _gen: 0,
    _appliedGen: 0,

    init() {
      const component = this.$root?.closest?.('[wire\\:id]')
      wireId = component?.getAttribute?.('wire:id') ?? null
      this._gen = this.contentGeneration()
      this._appliedGen = this._gen
      this._bumpGeneration = event => {
        const target = event?.target
        if (!target?.closest?.('[data-stored-task-board-query-control]')) return
        const changesOnSelection = target.matches('select, input[type="radio"], input[type="checkbox"]')
        if (event.type !== (changesOnSelection ? 'change' : 'input')) return

        this._gen += 1
        this._appliedGen = Math.max(this._appliedGen, this._gen)
        const generation = String(this._gen)
        const content = this.$root?.querySelector?.('[data-stored-task-board-content]')
        const board = this.$root?.querySelector?.(
          '[data-task-board-root][data-task-board-client-gen-required]'
        )
        if (content?.dataset) content.dataset.clientGen = generation
        if (board?.dataset) board.dataset.clientGen = generation
        try {
          this.livewire()?.set?.('clientGen', this._gen, false)
        } catch {
          // The matching query commit will fail closed if the component disappeared.
        }
      }
      this.$root?.addEventListener?.('input', this._bumpGeneration, true)
      this.$root?.addEventListener?.('change', this._bumpGeneration, true)

      if (typeof window.Livewire?.hook === 'function') {
        stopMorphGuard = window.Livewire.hook('morph.updating', ({ el, toEl, skip }) => {
          if (!el?.hasAttribute?.('data-stored-task-board-content')) return
          if (!this.$root?.contains?.(el)) return

          const incomingAttribute = toEl?.getAttribute?.('data-client-gen')
          if (incomingAttribute === null || incomingAttribute === undefined || incomingAttribute === '') {
            return
          }

          const incoming = Number(incomingAttribute)
          if (!Number.isSafeInteger(incoming) || incoming < 1) return
          if (incoming < this._appliedGen) {
            skip()

            return
          }
          this._appliedGen = incoming
          this._gen = Math.max(this._gen, incoming)
        })
      }
    },

    destroy() {
      this.$root?.removeEventListener?.('input', this._bumpGeneration, true)
      this.$root?.removeEventListener?.('change', this._bumpGeneration, true)
      stopMorphGuard?.()
      stopMorphGuard = null
      wireId = null
    },

    livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    contentGeneration() {
      const value = Number(
        this.$root?.querySelector?.('[data-stored-task-board-content]')?.dataset?.clientGen
      )

      return Number.isSafeInteger(value) && value >= 0 ? value : 0
    }
  }
}
